/**
 * Shared React-effect lifecycle for the three SAPIENT **geometry polygon**
 * map layers — field-of-view / coverage / obscuration (PR5b inc3 + inc4;
 * REQ-UI-070; ADR-032 §4.2). The single hook the per-kind layer components
 * (:file:`fov-layer.tsx`, :file:`coverage-layer.tsx`,
 * :file:`obscuration-layer.tsx`) delegate to, so the subscribe →
 * zoom-listener → attach/detach-gate logic exists **once** (CLAUDE.md §4.4 —
 * never duplicate logic across modules).
 *
 * # The gate (REQ-UI-070; CLAUDE.md §5.8)
 *
 * Geometry overlays are off-by-default AND zoom-gated. A layer attaches ONLY
 * when its operator ``visible`` toggle is on AND
 * ``map.getZoom() >= GEOMETRY_MIN_ZOOM`` (7); otherwise it detaches. The gate
 * is re-evaluated on the store-entries change, the ``visible`` flag change,
 * and the map's ``zoomend`` / ``moveend`` events.
 *
 * **Why no explicit viewport filter.** At zoom ≥ 7 the visible span is small
 * (the same threshold at which the snapshot layer declusters to individual
 * markers), so few platforms are in view, and MapLibre's WebGL renderer
 * culls off-screen polygons itself (only visible tiles paint). The zoom gate
 * plus MapLibre culling therefore bound the per-frame cost without a
 * client-side "is this platform in the viewport" pass — which would add
 * per-``moveend`` CPU and complexity for no benefit at this zoom. (At low
 * zoom the gate hides the layer entirely, so the dense all-platform overlay
 * never paints over the CONUS framing.)
 *
 * # Read-only on the store
 *
 * Each layer subscribes to the shared geometry store
 * (:func:`usePlatformGeometry`) but never writes it — the store is fed by the
 * status-geometry SSE bridge mounted ONCE in :file:`use-map-sse-bridges.ts`.
 * This hook therefore takes the already-read ``entries`` as a parameter (the
 * caller subscribes) and owns only the zoom-gate + attach/detach plumbing.
 *
 * Cross-references:
 *   - REQ-UI-070 (off-by-default, zoom-gated geometry) / REQ-UI-002 (WebGL).
 *   - ADR-032 §4.2 (Status geometry data model).
 *   - CLAUDE.md §5.8 (perf discipline) / §8.1 (operator visibility) /
 *     §4.4 (no duplicated logic).
 *   - map/track-tail-layer.tsx (the enabled-gated lifecycle this generalises).
 *   - map/geometry-polygon-layer.ts (the attach/detach the callbacks wrap).
 */
import { useEffect, useState } from "react";
import type { Map as MapLibreMap } from "maplibre-gl";
import type { PlatformGeometryEntry } from "@/map/geometry-store";

/**
 * Minimum zoom at which the geometry overlays render (REQ-UI-070 perf gate).
 * Matches the z7 decluster threshold at which the snapshot layer resolves
 * individual markers, so geometry appears exactly when the map is zoomed in
 * enough that few platforms are in view. Shared by all three geometry layers
 * (FoV / coverage / obscuration).
 */
export const GEOMETRY_MIN_ZOOM = 7;

/** Parameters for :func:`useGeometryLayerEffect`. */
export interface UseGeometryLayerEffectParams {
  /** Live MapLibre map instance (mounted after the map's ``load``). */
  readonly map: MapLibreMap;
  /** The per-platform geometry the caller read from the store. */
  readonly entries: readonly PlatformGeometryEntry[];
  /** The operator's per-kind visibility toggle (off-by-default). */
  readonly visible: boolean;
  /**
   * Attach (or idempotently update) this kind's polygon layer for the given
   * entries. Called only when the gate is open. Wraps the per-kind
   * :func:`attachGeometryPolygonLayer` call.
   */
  readonly attach: (map: MapLibreMap, entries: readonly PlatformGeometryEntry[]) => void;
  /**
   * Detach this kind's polygon layer. Called when the gate closes (toggle
   * off / zoom out) and on unmount. Wraps the per-kind
   * :func:`detachGeometryPolygonLayer` call.
   */
  readonly detach: (map: MapLibreMap) => void;
}

/**
 * Own the gated attach/detach lifecycle for one geometry polygon layer.
 * Returns nothing — the caller (a non-visual layer component) just invokes
 * it and returns ``null``.
 *
 * The three per-kind components call this with their own ``attach`` /
 * ``detach`` (bound to their IDs + colours) so the zoom-listener + gate
 * plumbing lives here once (CLAUDE.md §4.4). Equivalent to the inline effect
 * the inc3 FoV layer previously carried.
 */
export function useGeometryLayerEffect(params: UseGeometryLayerEffectParams): void {
  const { map, entries, visible, attach, detach } = params;

  // Track the map's current zoom so the gate re-evaluates as the operator
  // zooms across the GEOMETRY_MIN_ZOOM threshold. Seeded from the live map;
  // updated on ``zoomend`` / ``moveend``. Held in state so a threshold
  // crossing re-runs the attach/detach effect below.
  const [zoom, setZoom] = useState<number>(() => map.getZoom());
  useEffect(() => {
    const onMove = (): void => {
      setZoom(map.getZoom());
    };
    // ``moveend`` covers a flyTo/easeTo that changes zoom; ``zoomend`` covers
    // scroll/pinch zoom. Both are cheap (fire once per settled gesture). Sync
    // once on mount in case the zoom changed between the lazy initializer and
    // the listener attach.
    onMove();
    map.on("zoomend", onMove);
    map.on("moveend", onMove);
    return () => {
      map.off("zoomend", onMove);
      map.off("moveend", onMove);
    };
  }, [map]);

  // Detach once on unmount (or a rare ``map`` / ``detach`` change). The
  // per-kind callers pass a stable module-level ``detach`` (e.g.
  // :func:`detachFovLayer`), so in practice this fires only on unmount / map
  // swap; ``detach`` is in the deps to satisfy ``exhaustive-deps`` honestly.
  useEffect(() => {
    return () => {
      detach(map);
    };
  }, [map, detach]);

  // The perf gate. Attach (idempotent ``setData`` on the live source) ONLY
  // when this kind's toggle is on AND the map is zoomed to
  // >= GEOMETRY_MIN_ZOOM; otherwise detach so no polygon paints. Re-runs on:
  // ``visible`` change (toggle), ``entries`` change (new/updated geometry),
  // zoom change (``zoomend`` / ``moveend`` → ``zoom`` state), and the
  // (stable) ``attach`` / ``detach`` callbacks.
  useEffect(() => {
    const gateOpen = visible && zoom >= GEOMETRY_MIN_ZOOM;
    if (!gateOpen) {
      detach(map);
      return;
    }
    attach(map, entries);
  }, [map, entries, visible, zoom, attach, detach]);
}
